import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'

const LEGAL_DOCS = [
  { id:'baa',     title:'Business Associate Agreement', abbr:'BAA', description:'HIPAA-required. Must be signed by every clinic before they can use the Service with patient data.', urgency:'critical', file:'ClearPath_PT_BAA.docx', icon:'' },
  { id:'tos',     title:'Terms of Service',             abbr:'ToS', description:'Governing agreement for all users. Presented and accepted during clinic registration.',            urgency:'required', file:'ClearPath_PT_Terms_of_Service.docx', icon:'' },
  { id:'privacy', title:'Privacy Policy',               abbr:'PP',  description:'Includes HIPAA Notice of Privacy Practices. Published at /privacy on the public site.',           urgency:'required', file:'ClearPath_PT_Privacy_Policy.docx', icon:'' },
  { id:'msa',     title:'Subscription Agreement',       abbr:'MSA', description:'Master Service Agreement covering pricing, SLAs, liability, and data ownership.',                  urgency:'required', file:'ClearPath_PT_Subscription_Agreement.docx', icon:'' },
]

const COMPLIANCE_CHECKLIST = [
  { label: 'AWS BAA executed with Amazon',            done: true  },
  { label: 'RDS automated backups enabled (30-day)',  done: true  },
  { label: 'Audit logging active (audit_logs table)', done: true  },
  { label: 'CORS locked to frontend domains',         done: true  },
  { label: 'Rate limiting on auth endpoints',         done: true  },
  { label: 'clinic_id isolation on all PHI tables',   done: true  },
  { label: 'HIPAA Security Risk Assessment',          done: false },
  { label: 'Cyber liability insurance obtained',      done: false },
  { label: 'Business registered (LLC/Corp)',          done: false },
  { label: 'Attorney review of BAA completed',        done: false },
  { label: 'Attorney review of ToS/Privacy Policy',  done: false },
  { label: 'Stripe account PCI-DSS compliant',        done: false },
]

const ALL_FEATURES = [
  { id:'soap',          label:'SOAP Notes',              group:'clinical'  },
  { id:'intake',        label:'Intake Forms',            group:'clinical'  },
  { id:'appointments',  label:'Appointments',            group:'clinical'  },
  { id:'charts',        label:'Progress Charts',         group:'clinical'  },
  { id:'messaging',     label:'HIPAA Messaging',         group:'clinical'  },
  { id:'compliance',    label:'Exercise Compliance',     group:'addon'     },
  { id:'hep',           label:'HEP Builder',             group:'addon'     },
  { id:'pdf',           label:'PDF Reports',             group:'addon'     },
  { id:'caleb',         label:'Caleb AI Chat',           group:'ai'        },
  { id:'discharge',     label:'AI Discharge Prediction', group:'ai'        },
  { id:'soap_ai',       label:'AI SOAP Drafting',        group:'ai'        },
  { id:'injury3d',      label:'3D Injury Explorer',      group:'ai'        },
  { id:'plateau',       label:'Plateau Detection',       group:'analytics' },
  { id:'outcomes',      label:'Outcome Trend Reports',   group:'analytics' },
  { id:'staff',         label:'Staff Role Management',   group:'mgmt'      },
  { id:'multiloc',      label:'Multi-location Support',  group:'mgmt'      },
  { id:'custom_fields', label:'Custom Intake Fields',    group:'mgmt'      },
  { id:'branding',      label:'White-label Branding',    group:'mgmt'      },
]

const ALL_FEAT_IDS = ALL_FEATURES.map(f => f.id)

const PLAN_PRESETS = {
  trial:      { label:'Trial',      color:'#d97706', bg:'#fef3c7', features: ALL_FEAT_IDS,                                                                                              monthly: 0   },
  starter:    { label:'Starter',    color:'#1d4ed8', bg:'#dbeafe', features: ['soap','intake','appointments','charts','messaging'],                                                      monthly: 49  },
  pro:        { label:'Pro',        color:'#16a34a', bg:'#dcfce7', features: ['soap','intake','appointments','charts','messaging','compliance','hep','pdf','caleb','discharge','plateau'],monthly: 149 },
  enterprise: { label:'Enterprise', color:'#7c3aed', bg:'#ede9fe', features: ALL_FEAT_IDS,                                                                                              monthly: 299 },
  custom:     { label:'Custom',     color:'#0d9488', bg:'#f0fdfa', features: [],                                                                                                        monthly: 0   },
}

const API       = process.env.REACT_APP_API_URL        || 'http://localhost:8000'
const _saToken = localStorage.getItem('clearpath_token') || localStorage.getItem('token') || ''
const AH = _saToken ? { headers: { Authorization: 'Bearer ' + _saToken } } : {}

const C = {
  pine:'#1a3d2e', forest:'#2d5a3d', sage:'#4a7c59', fern:'#6aab7a', mint:'#a8d5b0', foam:'#e8f5eb',
  cream:'#faf9f6', white:'#ffffff', ink:'#111815', inkMid:'#3d4f45', inkLt:'#7a8c81', border:'#dde8e0',
  amber:'#d97706', amberLt:'#fef3c7', red:'#dc2626', redLt:'#fee2e2',
  blue:'#1d4ed8',  blueLt:'#dbeafe', purple:'#7c3aed', purpleLt:'#ede9fe',
  green:'#16a34a', greenLt:'#dcfce7', teal:'#0d9488', tealLt:'#f0fdfa',
}
const DISPLAY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO    = "'DM Mono', 'Fira Mono', monospace"

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital,wght@0,400;1,400&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin   { to { transform: rotate(360deg); } }
  body { background: ${C.cream}; }
  .sa-page { animation: fadeUp .4s cubic-bezier(.22,1,.36,1) both; }
  .stat-card { background:white; border:1.5px solid ${C.border}; border-radius:16px; padding:20px 22px; transition:all .2s; }
  .stat-card:hover { border-color:${C.mint}; transform:translateY(-2px); box-shadow:0 8px 28px rgba(26,61,46,.07); }
  .clinic-row { transition:background .12s; }
  .clinic-row:hover { background:${C.foam}; }
  .plan-badge { display:inline-flex; align-items:center; padding:3px 9px; border-radius:99px; font-size:11px; font-weight:700; letter-spacing:.04em; white-space:nowrap; }
  .feat-chip  { display:inline-flex; align-items:center; padding:2px 7px; border-radius:99px; font-size:10px; font-weight:700; background:${C.foam}; color:${C.sage}; border:1px solid ${C.mint}; white-space:nowrap; }
  .sa-input { padding:6px 8px; font-size:13px; border:1.5px solid ${C.border}; border-radius:8px; font-family:${BODY}; outline:none; transition:border-color .15s; background:white; }
  .sa-input:focus { border-color:${C.sage}; }
  .sa-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border-radius:8px; font-size:12px; font-weight:700; font-family:${BODY}; cursor:pointer; border:none; transition:all .15s; }
  .tab-btn { padding:10px 18px; font-family:${BODY}; font-size:13px; font-weight:600; border:none; cursor:pointer; background:none; transition:all .15s; }
  .waitlist-row { border-bottom:1px solid ${C.border}; }
  .waitlist-row:last-child { border-bottom:none; }
  .waitlist-row:hover { background:${C.cream}; }
  @media (max-width:900px) {
    .sa-stats-grid { grid-template-columns:1fr 1fr !important; }
    .clinic-desktop { display:none !important; }
    .clinic-mobile  { display:flex !important; }
  }
  @media (min-width:901px) { .clinic-mobile { display:none !important; } }
  @media (max-width:540px) {
    .sa-main { padding:20px 14px 60px !important; }
    .sa-stats-grid { grid-template-columns:1fr 1fr !important; }
  }
`

//  Sub-components 

function PlanBadge({ plan }) {
  const p = PLAN_PRESETS[plan] || PLAN_PRESETS.trial
  return <span className="plan-badge" style={{ color: p.color, background: p.bg }}>{p.label}</span>
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 38, color: color || C.ink, lineHeight: 1, marginBottom: 6 }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: C.inkLt, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

function Spinner() {
  return <div style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${C.border}`, borderTop: `2px solid ${C.purple}`, margin: '0 auto', animation: 'spin .8s linear infinite' }} />
}

//  Main Component 

export default function SuperAdmin({ onLogout }) {
  const [clinics,       setClinics]       = useState([])
  const [loading,       setLoading]       = useState(true)
  const [loadError,     setLoadError]     = useState('')
  const [activeTab,     setActiveTab]     = useState('clinics')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [expandedRow,   setExpandedRow]   = useState(null)
  const [editingClinic, setEditingClinic] = useState(null)
  const [saving,        setSaving]        = useState(false)
  const [clinicSearch,  setClinicSearch]  = useState('')
  const [clinicFilter,  setClinicFilter]  = useState('all')

  // Audit logs
  const [auditLogs,    setAuditLogs]    = useState([])
  const [auditTotal,   setAuditTotal]   = useState(0)
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditOffset,  setAuditOffset]  = useState(0)
  const [auditFilter,  setAuditFilter]  = useState({ clinic_id: '', username: '', action: '' })
  const AUDIT_LIMIT = 50

  // Waitlist
  const [waitlist,        setWaitlist]        = useState([])
  const [waitlistLoading, setWaitlistLoading] = useState(false)

  // Agreements
  const [agreements,        setAgreements]        = useState([])
  const [agreementsLoading, setAgreementsLoading] = useState(false)

  useEffect(() => { fetchClinics() }, [])

  //  Fetchers 

  const fetchClinics = () => {
    setLoading(true); setLoadError('')
    axios.get(API + '/admin/clinics', AH)
      .then(r => setClinics(r.data))
      .catch(e => setLoadError('Failed to load clinics: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false))
  }

  const fetchAuditLogs = (offset = 0, append = false) => {
    setAuditLoading(true)
    const p = new URLSearchParams({ limit: AUDIT_LIMIT, offset })
    if (auditFilter.clinic_id) p.append('clinic_id', auditFilter.clinic_id)
    if (auditFilter.username)  p.append('username',  auditFilter.username)
    if (auditFilter.action)    p.append('action',    auditFilter.action)
    axios.get(`${API}/admin/audit-logs?${p}`, AH)
      .then(r => {
        setAuditTotal(r.data.total)
        setAuditLogs(prev => append ? [...prev, ...r.data.logs] : r.data.logs)
        setAuditOffset(offset + r.data.logs.length)
      })
      .catch(e => alert('Failed to load audit logs: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setAuditLoading(false))
  }

  const fetchWaitlist = () => {
    setWaitlistLoading(true)
    axios.get(API + '/admin/waitlist', AH)
      .then(r => setWaitlist(r.data.signups || r.data || []))
      .catch(e => alert('Failed to load waitlist: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setWaitlistLoading(false))
  }

  const fetchAgreements = () => {
    setAgreementsLoading(true)
    axios.get(API + '/admin/agreements', AH)
      .then(r => setAgreements(r.data))
      .catch(e => alert('Failed to load agreements: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setAgreementsLoading(false))
  }

  //  Clinic actions 

  const toggleClinic = (id) =>
    axios.put(`${API}/admin/clinics/${id}/toggle`, {}, AH)
      .then(() => fetchClinics())
      .catch(() => alert('Failed to update clinic'))

  const deleteClinic = (id) =>
    axios.delete(`${API}/admin/clinics/${id}`, AH)
      .then(() => { fetchClinics(); setExpandedRow(null) })
      .catch(() => alert('Failed to delete clinic'))

  const extendTrial = (id, days = 14) =>
    axios.put(`${API}/admin/clinics/${id}/trial-extend?days=${days}`, {}, AH)
      .then(() => fetchClinics())
      .catch(e => alert('Failed to extend trial: ' + (e.response?.data?.detail || e.message)))

  const saveClinicEdit = () => {
    if (!editingClinic) return
    setSaving(true)
    const { id, plan, monthly_amount, features_enabled } = editingClinic
    Promise.all([
      axios.put(`${API}/admin/clinics/${id}/plan?plan=${plan}`, {}, AH),
      axios.put(`${API}/admin/clinics/${id}/monthly-amount?amount=${monthly_amount || 0}`, {}, AH),
      axios.put(`${API}/admin/clinics/${id}/features`, features_enabled, AH),
    ])
      .then(() => { fetchClinics(); setEditingClinic(null) })
      .catch(e => alert('Save failed: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setSaving(false))
  }

  //  Derived stats 

  const totalPatients = clinics.reduce((a, c) => a + (c.patient_count || 0), 0)
  const activeCount   = clinics.filter(c => c.is_active).length
  const trialCount    = clinics.filter(c => c.plan === 'trial').length
  const paidClinics   = clinics.filter(c => c.plan !== 'trial' || (c.monthly_amount && c.monthly_amount > 0))
  const mrr           = clinics.reduce((a, c) => a + (c.monthly_amount || 0), 0)
  const arr           = mrr * 12

  const trialDaysLeft = (expiresAt) => {
    if (!expiresAt) return null
    return Math.ceil((new Date(expiresAt) - new Date()) / 86400000)
  }

  const filteredClinics = useMemo(() => {
    let list = [...clinics]
    if (clinicSearch.trim()) {
      const q = clinicSearch.toLowerCase()
      list = list.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.slug?.toLowerCase().includes(q) ||
        c.owner_email?.toLowerCase().includes(q)
      )
    }
    if (clinicFilter === 'active')  list = list.filter(c => c.is_active)
    if (clinicFilter === 'trial')   list = list.filter(c => c.plan === 'trial')
    if (clinicFilter === 'paid')    list = list.filter(c => c.monthly_amount > 0)
    if (clinicFilter === 'expired') list = list.filter(c => { const d = trialDaysLeft(c.trial_expires_at); return d !== null && d <= 0 })
    return list
  }, [clinics, clinicSearch, clinicFilter])

  const tabSwitch = (id) => {
    setActiveTab(id)
    if (id === 'audit')    { setAuditLogs([]); setAuditOffset(0); fetchAuditLogs(0) }
    if (id === 'waitlist') fetchWaitlist()
    if (id === 'legal')    fetchAgreements()
  }

  //  Render 

  const TABS = [
    { id: 'clinics',  label: '  Clinics'    },
    { id: 'revenue',  label: '  Revenue'     },
    { id: 'waitlist', label: '  Waitlist'    },
    { id: 'audit',    label: '  Audit Logs'  },
    { id: 'legal',    label: '  Legal'      },
  ]

  return (
    <div style={{ fontFamily: BODY, background: C.cream, minHeight: '100vh' }}>
      <style>{CSS}</style>

      {/*  DELETE CONFIRM MODAL  */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}></div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: C.ink, margin: '0 0 8px' }}>Delete {confirmDelete.name}?</h3>
            <p style={{ fontSize: 13, color: C.inkLt, margin: '0 0 6px', lineHeight: 1.6 }}>
              Permanently deletes all users, patients, sessions, and data for <strong>{confirmDelete.name}</strong>. Code <span style={{ fontFamily: MONO, fontWeight: 700, color: C.ink }}>{confirmDelete.slug}</span> will be freed.
            </p>
            <p style={{ fontSize: 12, color: C.red, margin: '0 0 22px' }}>This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmDelete(null)} className="sa-btn" style={{ flex: 1, background: 'white', border: `1.5px solid ${C.border}`, color: C.inkMid, justifyContent: 'center' }}>Cancel</button>
              <button onClick={() => { deleteClinic(confirmDelete.id); setConfirmDelete(null) }} className="sa-btn" style={{ flex: 1, background: C.red, color: 'white', justifyContent: 'center' }}>Delete Clinic</button>
            </div>
          </div>
        </div>
      )}

      {/*  EDIT CLINIC MODAL  */}
      {editingClinic && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 32, maxWidth: 580, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,.25)', margin: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.sage, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Edit Clinic</div>
                <h3 style={{ fontSize: 19, fontWeight: 700, color: C.ink, margin: 0 }}>{editingClinic.name}</h3>
              </div>
              <button onClick={() => setEditingClinic(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.inkLt }}>×</button>
            </div>

            {/* Plan selector */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 8 }}>Plan</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(PLAN_PRESETS).map(([key, p]) => (
                  <button key={key} onClick={() => {
                    setEditingClinic(prev => ({
                      ...prev,
                      plan: key,
                      monthly_amount: key === 'custom' ? (prev.monthly_amount || 0) : p.monthly,
                      features_enabled: key === 'custom' ? prev.features_enabled : [...p.features],
                    }))
                  }} style={{
                    padding: '6px 14px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                    border: `2px solid ${editingClinic.plan === key ? p.color : C.border}`,
                    background: editingClinic.plan === key ? p.bg : 'white',
                    color: editingClinic.plan === key ? p.color : C.inkLt,
                    cursor: 'pointer', fontFamily: BODY, transition: 'all .15s',
                  }}>{p.label}</button>
                ))}
              </div>
            </div>

            {/* Monthly amount */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 8 }}>Monthly Revenue (MRR)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18, color: C.inkLt }}>$</span>
                <input type="number" min="0" value={editingClinic.monthly_amount || 0}
                  onChange={e => setEditingClinic(prev => ({ ...prev, monthly_amount: parseFloat(e.target.value) || 0 }))}
                  className="sa-input" style={{ width: 120 }} />
                <span style={{ fontSize: 13, color: C.inkLt }}>/month</span>
              </div>
            </div>

            {/* Features */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  Features Enabled ({(editingClinic.features_enabled || []).length})
                </label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setEditingClinic(p => ({ ...p, features_enabled: ALL_FEAT_IDS }))} style={{ fontSize: 11, fontWeight: 700, color: C.sage, background: 'none', border: 'none', cursor: 'pointer' }}>All</button>
                  <button onClick={() => setEditingClinic(p => ({ ...p, features_enabled: [] }))} style={{ fontSize: 11, fontWeight: 700, color: C.red, background: 'none', border: 'none', cursor: 'pointer' }}>None</button>
                </div>
              </div>
              {['clinical', 'addon', 'ai', 'analytics', 'mgmt'].map(group => (
                <div key={group} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
                    {{ clinical: 'Core Clinical', addon: 'Add-ons', ai: 'AI Features', analytics: 'Analytics', mgmt: 'Practice Mgmt' }[group]}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ALL_FEATURES.filter(f => f.group === group).map(f => {
                      const on = (editingClinic.features_enabled || []).includes(f.id)
                      return (
                        <button key={f.id} onClick={() => setEditingClinic(p => ({
                          ...p,
                          features_enabled: on
                            ? p.features_enabled.filter(x => x !== f.id)
                            : [...(p.features_enabled || []), f.id],
                        }))} style={{
                          padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                          border: `1.5px solid ${on ? C.sage : C.border}`,
                          background: on ? C.foam : 'white',
                          color: on ? C.sage : C.inkLt,
                          cursor: 'pointer', fontFamily: BODY, transition: 'all .12s',
                        }}>{f.label}</button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEditingClinic(null)} className="sa-btn" style={{ flex: 1, background: 'white', border: `1.5px solid ${C.border}`, color: C.inkMid, justifyContent: 'center' }}>Cancel</button>
              <button onClick={saveClinicEdit} disabled={saving} className="sa-btn" style={{ flex: 2, background: C.pine, color: 'white', justifyContent: 'center', opacity: saving ? 0.8 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  TOP NAV  */}
      <nav style={{ background: C.pine, height: 60, display: 'flex', alignItems: 'center', padding: '0 28px', position: 'sticky', top: 0, zIndex: 100, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: C.sage, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
              <path d="M16 4C16 4 9 9 9 17c0 3.9 3.1 7 7 7s7-3.1 7-7c0-8-7-13-7-13Z" stroke="white" strokeWidth="2" fill="none" strokeLinejoin="round"/>
              <line x1="16" y1="24" x2="16" y2="28" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <line x1="13" y1="27" x2="19" y2="27" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'white', letterSpacing: '-.02em', lineHeight: 1.1, fontFamily: DISPLAY }}>ClearPath PT</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Super Admin</div>
          </div>
        </div>
        <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.75)', padding: '8px 16px', borderRadius: 99, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: BODY }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Sign out
        </button>
      </nav>

      <div className="sa-main" style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 28px 80px' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 32, borderBottom: `2px solid ${C.border}`, overflowX: 'auto' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => tabSwitch(tab.id)} className="tab-btn" style={{
              color: activeTab === tab.id ? C.purple : C.inkLt,
              borderBottom: activeTab === tab.id ? `3px solid ${C.purple}` : '3px solid transparent',
              marginBottom: -2, whiteSpace: 'nowrap',
            }}>{tab.label}</button>
          ))}
        </div>

        {/* 
            CLINICS TAB
         */}
        {activeTab === 'clinics' && (
          <div className="sa-page">
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.sage, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>Dashboard</p>
              <h1 style={{ fontFamily: DISPLAY, fontSize: 34, fontWeight: 400, color: C.ink, lineHeight: 1.15 }}>Clinic <em>Registry</em></h1>
              <p style={{ fontSize: 14, color: C.inkLt, marginTop: 4 }}>{activeCount} active · {clinics.length} total · {totalPatients?.toLocaleString() ?? '0'} patients</p>
            </div>

            {/* Stats */}
            <div className="sa-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 28 }}>
              <StatCard label="Total Clinics"  value={clinics.length} />
              <StatCard label="Active"         value={activeCount}    color={C.green} />
              <StatCard label="On Trial"       value={trialCount}     color={C.amber} />
              <StatCard label="Total Patients" value={totalPatients?.toLocaleString() ?? '0'} color={C.pine} />
              <StatCard label="Est. MRR"       value={'$' + mrr?.toLocaleString() ?? '0'} color={C.teal} sub={paidClinics.length + ' paid'} />
            </div>

            {/* Search + filter */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <input placeholder="Search by name, code, or email…" value={clinicSearch}
                onChange={e => setClinicSearch(e.target.value)}
                className="sa-input" style={{ flex: 1, minWidth: 200 }} />
              {['all', 'active', 'trial', 'paid', 'expired'].map(f => (
                <button key={f} onClick={() => setClinicFilter(f)} className="sa-btn" style={{
                  background: clinicFilter === f ? C.pine : 'white',
                  color: clinicFilter === f ? 'white' : C.inkLt,
                  border: `1.5px solid ${clinicFilter === f ? C.pine : C.border}`,
                }}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <button onClick={fetchClinics} className="sa-btn" style={{ background: 'white', border: `1.5px solid ${C.border}`, color: C.inkLt }}>↻ Refresh</button>
            </div>

            {/* Clinic table */}
            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, overflow: 'hidden', marginBottom: 24 }}>

              {/* Desktop column headers */}
              <div className="clinic-desktop" style={{ display: 'grid', gridTemplateColumns: '2fr 80px 160px 60px 60px 60px 110px 80px 190px', gap: 8, padding: '10px 20px', background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                {['Clinic', 'Code', 'Owner', 'Pts', 'Users', 'Sess.', 'Plan', 'MRR', 'Actions'].map(h => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.1em' }}>{h}</div>
                ))}
              </div>

              {loading && <div style={{ padding: 48, textAlign: 'center' }}><Spinner /><p style={{ marginTop: 14, fontSize: 13, color: C.inkLt }}>Loading clinics…</p></div>}

              {!loading && loadError && (
                <div style={{ padding: '28px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.red, marginBottom: 4 }}>Could not load clinics</div>
                    <div style={{ fontSize: 12, color: C.inkLt }}>{loadError}</div>
                  </div>
                  <button onClick={() => { setLoadError(''); fetchClinics() }} className="sa-btn" style={{ background: C.purpleLt, color: C.purple, border: `1.5px solid ${C.purple}` }}>Retry</button>
                </div>
              )}

              {!loading && !loadError && filteredClinics.length === 0 && (
                <div style={{ padding: 56, textAlign: 'center', color: C.inkLt, fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 15 }}>
                  {clinics.length === 0 ? 'No clinics registered yet.' : 'No clinics match your filter.'}
                </div>
              )}

              {filteredClinics.map((c, i) => {
                const daysLeft     = trialDaysLeft(c.trial_expires_at)
                const trialExpired = daysLeft !== null && daysLeft <= 0
                const isExpanded   = expandedRow === c.id

                return (
                  <div key={c.id} className="clinic-row" style={{ borderBottom: i < filteredClinics.length - 1 ? `1px solid ${C.border}` : 'none' }}>

                    {/* Desktop row */}
                    <div className="clinic-desktop" style={{ display: 'grid', gridTemplateColumns: '2fr 80px 160px 60px 60px 60px 110px 80px 190px', gap: 8, padding: '14px 20px', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setExpandedRow(isExpanded ? null : c.id)}>

                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: C.inkLt, fontFamily: MONO, marginTop: 2 }}>
                          #{c.id} · {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                          {daysLeft !== null && (
                            <span style={{ marginLeft: 6, fontWeight: 700, color: trialExpired ? C.red : daysLeft <= 7 ? C.amber : C.inkLt }}>
                              · {trialExpired ? 'Trial expired' : `${daysLeft}d left`}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ fontSize: 13, fontWeight: 700, color: C.sage, fontFamily: MONO }}>{c.slug}</div>
                      <div style={{ fontSize: 12, color: C.inkMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.owner_email}>{c.owner_email || '—'}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{c.patient_count ?? 0}</div>
                      <div style={{ fontSize: 13, color: C.inkLt }}>{c.user_count ?? 0}</div>
                      <div style={{ fontSize: 13, color: C.inkLt }}>{c.session_count ?? 0}</div>
                      <PlanBadge plan={c.plan} />
                      <div style={{ fontSize: 13, fontWeight: 700, color: c.monthly_amount > 0 ? C.teal : C.inkLt }}>
                        {c.monthly_amount > 0 ? `$${c.monthly_amount}/mo` : '—'}
                      </div>

                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setEditingClinic({ ...c, features_enabled: c.features_enabled || [] })}
                          className="sa-btn" style={{ background: C.blueLt, color: C.blue, fontSize: 11, padding: '5px 10px' }}> Edit</button>
                        <button onClick={() => toggleClinic(c.id)}
                          className="sa-btn" style={{ background: c.is_active ? C.redLt : C.greenLt, color: c.is_active ? C.red : C.green, fontSize: 11, padding: '5px 10px' }}>
                          {c.is_active ? 'Suspend' : 'Activate'}
                        </button>
                        <button onClick={() => setConfirmDelete(c)}
                          className="sa-btn" style={{ background: C.redLt, color: C.red, fontSize: 11, padding: '5px 8px' }}></button>
                      </div>
                    </div>

                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <div style={{ padding: '18px 20px 22px', background: C.foam, borderTop: `1px solid ${C.border}` }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 16, marginBottom: 16 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Status</div>
                            <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700, background: c.is_active ? C.greenLt : C.redLt, color: c.is_active ? C.green : C.red }}>
                              {c.is_active ? 'Active' : 'Suspended'}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Trial Expires</div>
                            <div style={{ fontSize: 13, color: C.ink }}>{c.trial_expires_at ? new Date(c.trial_expires_at).toLocaleDateString() : '—'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Sessions Logged</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{(c.session_count || 0)?.toLocaleString() ?? '0'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Contact Owner</div>
                            {c.owner_email
                              ? <a href={`mailto:${c.owner_email}`} style={{ fontSize: 13, color: C.teal, textDecoration: 'none', fontWeight: 600 }}> {c.owner_email}</a>
                              : <span style={{ fontSize: 13, color: C.inkLt }}>—</span>}
                          </div>
                        </div>

                        {/* Features */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                            Features Enabled ({(c.features_enabled || []).length}/{ALL_FEAT_IDS.length})
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {(c.features_enabled || []).length === 0
                              ? <span style={{ fontSize: 12, color: C.inkLt, fontStyle: 'italic' }}>None — click Edit to configure</span>
                              : (c.features_enabled || []).map(fid => {
                                  const feat = ALL_FEATURES.find(f => f.id === fid)
                                  return <span key={fid} className="feat-chip">{feat?.label || fid}</span>
                                })
                            }
                          </div>
                        </div>

                        {/* Trial extend */}
                        {c.plan === 'trial' && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.inkLt }}>Extend trial:</span>
                            {[7, 14, 30].map(days => (
                              <button key={days} onClick={() => extendTrial(c.id, days)}
                                className="sa-btn" style={{ background: C.amberLt, color: C.amber, border: `1.5px solid ${C.amber}`, fontSize: 11 }}>
                                +{days} days
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mobile card */}
                    <div className="clinic-mobile" style={{ display: 'none', flexDirection: 'column', gap: 12, padding: '16px 18px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: C.sage, fontFamily: MONO, marginTop: 2 }}>{c.slug}</div>
                        </div>
                        <PlanBadge plan={c.plan} />
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 13, color: C.inkMid }}>
                        <span><strong style={{ color: C.ink }}>{c.patient_count}</strong> pts</span>
                        <span><strong style={{ color: C.ink }}>{c.session_count || 0}</strong> sessions</span>
                        {daysLeft !== null && <span style={{ color: trialExpired ? C.red : daysLeft <= 7 ? C.amber : C.inkLt, fontWeight: 600 }}>{trialExpired ? 'Expired' : `${daysLeft}d left`}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button onClick={() => setEditingClinic({ ...c, features_enabled: c.features_enabled || [] })} className="sa-btn" style={{ background: C.blueLt, color: C.blue, fontSize: 11 }}> Edit</button>
                        <button onClick={() => toggleClinic(c.id)} className="sa-btn" style={{ background: c.is_active ? C.redLt : C.greenLt, color: c.is_active ? C.red : C.green, fontSize: 11 }}>{c.is_active ? 'Suspend' : 'Activate'}</button>
                        <button onClick={() => setConfirmDelete(c)} className="sa-btn" style={{ background: C.redLt, color: C.red, fontSize: 11 }}> Delete</button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* 
            REVENUE TAB
         */}
        {activeTab === 'revenue' && (
          <div className="sa-page">
            <div style={{ marginBottom: 28 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.sage, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>Finance</p>
              <h1 style={{ fontFamily: DISPLAY, fontSize: 34, fontWeight: 400, color: C.ink, lineHeight: 1.15 }}>Revenue <em>Dashboard</em></h1>
            </div>

            {/* Key metrics */}
            <div className="sa-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 28 }}>
              <StatCard label="MRR"              value={'$' + mrr?.toLocaleString() ?? '0'}  color={C.teal}   sub="Monthly Recurring Revenue" />
              <StatCard label="ARR"              value={'$' + arr?.toLocaleString() ?? '0'}  color={C.pine}   sub="Annualized Run Rate" />
              <StatCard label="Paying Clinics"   value={paidClinics.length}           color={C.green}  sub={`of ${clinics.length} total`} />
              <StatCard label="Conversion Rate"  value={clinics.length > 0 ? Math.round(paidClinics.length / clinics.length * 100) + '%' : '—'} color={C.purple} sub="trial → paid" />
            </div>

            {/* Plan breakdown */}
            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, background: C.cream }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Revenue by Plan</span>
              </div>
              {Object.entries(PLAN_PRESETS).map(([key, p]) => {
                const planClinics = clinics.filter(c => c.plan === key)
                const planMRR     = planClinics.reduce((a, c) => a + (c.monthly_amount || 0), 0)
                const pct         = mrr > 0 ? Math.round(planMRR / mrr * 100) : 0
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '13px 24px', borderBottom: `1px solid ${C.border}` }}>
                    <span className="plan-badge" style={{ color: p.color, background: p.bg, minWidth: 80, justifyContent: 'center' }}>{p.label}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ height: 8, borderRadius: 99, background: C.foam, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: p.color, borderRadius: 99 }} />
                      </div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.ink, minWidth: 70, textAlign: 'right' }}>{planClinics.length} clinic{planClinics.length !== 1 ? 's' : ''}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.teal, minWidth: 80, textAlign: 'right' }}>${planMRR?.toLocaleString() ?? '0'}/mo</span>
                  </div>
                )
              })}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 24px', background: C.cream }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink, minWidth: 80 }}>TOTAL</span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink, minWidth: 70, textAlign: 'right' }}>{clinics.length} clinic{clinics.length !== 1 ? 's' : ''}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.teal, minWidth: 80, textAlign: 'right' }}>${mrr?.toLocaleString() ?? '0'}/mo</span>
              </div>
            </div>

            {/* Trials expiring soon */}
            {(() => {
              const expiringSoon = clinics.filter(c => {
                const d = trialDaysLeft(c.trial_expires_at)
                return d !== null && d >= 0 && d <= 14
              }).sort((a, b) => new Date(a.trial_expires_at) - new Date(b.trial_expires_at))
              if (expiringSoon.length === 0) return null
              return (
                <div style={{ background: 'white', border: `1.5px solid ${C.amber}`, borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
                  <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.border}`, background: C.amberLt, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16 }}>⏰</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>Trials Expiring in 14 Days ({expiringSoon.length})</span>
                    <span style={{ fontSize: 12, color: C.amber, marginLeft: 'auto' }}>Great time to follow up!</span>
                  </div>
                  {expiringSoon.map((c, i) => {
                    const d = trialDaysLeft(c.trial_expires_at)
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 24px', borderBottom: i < expiringSoon.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: C.inkLt }}>{c.owner_email}</div>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: d <= 3 ? C.red : C.amber }}>{d}d left</span>
                        {c.owner_email && (
                          <a href={`mailto:${c.owner_email}?subject=Your ClearPath PT trial is ending soon`}
                            style={{ fontSize: 11, fontWeight: 700, color: C.teal, textDecoration: 'none', background: C.tealLt, padding: '4px 10px', borderRadius: 8 }}>
                             Email
                          </a>
                        )}
                        <button onClick={() => extendTrial(c.id, 14)} className="sa-btn" style={{ background: C.amberLt, color: C.amber, border: `1px solid ${C.amber}`, fontSize: 11 }}>+14 days</button>
                      </div>
                    )
                  })}
                </div>
              )
            })()}

            {/* Paid clinics revenue detail */}
            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, background: C.cream }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Paid Clinics — Revenue Detail</span>
              </div>
              {paidClinics.length === 0
                ? <div style={{ padding: 40, textAlign: 'center', color: C.inkLt, fontFamily: DISPLAY, fontStyle: 'italic' }}>No paid clinics yet.</div>
                : paidClinics.map((c, i) => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '13px 24px', borderBottom: i < paidClinics.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: C.inkLt }}>{c.owner_email} · {c.patient_count} patients · {c.session_count || 0} sessions</div>
                    </div>
                    <PlanBadge plan={c.plan} />
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.teal }}>${(c.monthly_amount || 0)?.toLocaleString() ?? '0'}<span style={{ fontSize: 11, color: C.inkLt, fontWeight: 500 }}>/mo</span></span>
                    <span style={{ fontSize: 12, color: C.inkLt }}>${((c.monthly_amount || 0) * 12)?.toLocaleString() ?? '0'}/yr</span>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* 
            WAITLIST TAB
         */}
        {activeTab === 'waitlist' && (
          <div className="sa-page">
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.sage, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>Growth</p>
              <h1 style={{ fontFamily: DISPLAY, fontSize: 34, fontWeight: 400, color: C.ink, lineHeight: 1.15 }}>Waitlist <em>Signups</em></h1>
              <p style={{ fontSize: 14, color: C.inkLt, marginTop: 4 }}>{waitlist.length} clinic{waitlist.length !== 1 ? 's' : ''} waiting for access.</p>
            </div>

            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, overflow: 'hidden' }}>
              <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.border}`, background: C.cream, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>All Signups</span>
                <button onClick={fetchWaitlist} className="sa-btn" style={{ background: 'white', border: `1.5px solid ${C.border}`, color: C.inkLt, fontSize: 12 }}>↻ Refresh</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 160px 110px', gap: 0, padding: '9px 24px', background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                {['Name', 'Email', 'Clinic', 'Signed Up'].map(h => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em' }}>{h}</div>
                ))}
              </div>

              {waitlistLoading && <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>}
              {!waitlistLoading && waitlist.length === 0 && (
                <div style={{ padding: 48, textAlign: 'center', color: C.inkLt, fontFamily: DISPLAY, fontStyle: 'italic' }}>No waitlist signups yet.</div>
              )}
              {waitlist.map((w, i) => (
                <div key={w.id || i} className="waitlist-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 160px 110px', gap: 0, padding: '12px 24px', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{w.name}</div>
                  <a href={`mailto:${w.email}?subject=You%27re invited to ClearPath PT&body=Hi ${encodeURIComponent(w.name)}%2C%0A%0AYour spot is ready!`}
                    style={{ fontSize: 13, color: C.teal, textDecoration: 'none', fontWeight: 500 }}>{w.email}</a>
                  <div style={{ fontSize: 12, color: C.inkMid }}>{w.clinic_name || '—'}</div>
                  <div style={{ fontSize: 11, color: C.inkLt, fontFamily: MONO }}>{w.created_at ? new Date(w.created_at).toLocaleDateString() : '—'}</div>
                </div>
              ))}
            </div>

            {waitlist.length > 0 && (
              <div style={{ marginTop: 16, padding: '14px 20px', background: C.foam, border: `1.5px solid ${C.mint}`, borderRadius: 14, fontSize: 13, color: C.inkMid }}>
                 <strong>Tip:</strong> Click any email address to open a pre-filled invitation email in your mail client.
              </div>
            )}
          </div>
        )}

        {/* 
            AUDIT LOGS TAB
         */}
        {activeTab === 'audit' && (
          <div className="sa-page">
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.sage, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>HIPAA</p>
              <h1 style={{ fontFamily: DISPLAY, fontSize: 34, fontWeight: 400, color: C.ink, lineHeight: 1.15 }}>Audit <em>Logs</em></h1>
              <p style={{ fontSize: 14, color: C.inkLt, marginTop: 4 }}>All PHI access events. Total: <strong>{auditTotal?.toLocaleString() ?? '0'}</strong> records.</p>
            </div>

            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 16, padding: '18px 22px', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {[['Clinic ID', 'clinic_id', '90px', 'number', 'e.g. 3'], ['Username', 'username', '160px', 'text', 'Search username'], ['Action / Endpoint', 'action', '200px', 'text', 'e.g. GET /patients']].map(([label, key, w, type, ph]) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</label>
                  <input type={type} placeholder={ph} value={auditFilter[key]}
                    onChange={e => setAuditFilter(f => ({ ...f, [key]: e.target.value }))}
                    className="sa-input" style={{ width: w }} />
                </div>
              ))}
              <button onClick={() => { setAuditLogs([]); setAuditOffset(0); fetchAuditLogs(0) }}
                className="sa-btn" style={{ background: C.pine, color: 'white' }}>
                {auditLoading ? 'Loading…' : 'Search'}
              </button>
              <button onClick={() => { setAuditFilter({ clinic_id: '', username: '', action: '' }); setAuditLogs([]); setAuditOffset(0); setTimeout(() => fetchAuditLogs(0), 0) }}
                className="sa-btn" style={{ background: C.foam, color: C.sage, border: `1.5px solid ${C.mint}` }}>
                Clear
              </button>
            </div>

            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 60px 130px 80px 1fr 60px', gap: 0, padding: '10px 18px', background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                {['Timestamp', 'Clinic', 'Username', 'Method', 'Endpoint', 'Status'].map(h => (
                  <span key={h} style={{ fontSize: 10, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.08em' }}>{h}</span>
                ))}
              </div>

              {auditLoading && auditLogs.length === 0
                ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
                : auditLogs.length === 0
                  ? <div style={{ padding: 40, textAlign: 'center', color: C.inkLt, fontFamily: DISPLAY, fontStyle: 'italic' }}>No logs found. Click Search to load.</div>
                  : auditLogs.map((log, i) => (
                    <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '160px 60px 130px 80px 1fr 60px', gap: 0, padding: '10px 18px', borderBottom: i < auditLogs.length - 1 ? `1px solid ${C.border}` : 'none', background: i % 2 === 0 ? 'white' : C.cream }}>
                      <span style={{ fontSize: 11, color: C.inkLt, fontFamily: MONO }}>{log.timestamp ? new Date(log.timestamp)?.toLocaleString() ?? '0' : '—'}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>{log.clinic_id ?? '—'}</span>
                      <span style={{ fontSize: 12, color: C.inkMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.username || '—'}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: MONO, color: log.http_method === 'GET' ? C.blue : log.http_method === 'DELETE' ? C.red : C.sage }}>{log.http_method || '—'}</span>
                      <span style={{ fontSize: 11, color: C.inkMid, fontFamily: MONO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.endpoint || log.action || '—'}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: log.status_code >= 400 ? C.red : log.status_code >= 300 ? C.amber : C.green }}>{log.status_code ?? '—'}</span>
                    </div>
                  ))
              }
            </div>

            {auditLogs.length < auditTotal && auditLogs.length > 0 && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button onClick={() => fetchAuditLogs(auditOffset, true)} disabled={auditLoading}
                  className="sa-btn" style={{ background: C.foam, color: C.sage, border: `1.5px solid ${C.mint}`, padding: '10px 28px' }}>
                  {auditLoading ? 'Loading…' : `Load more (${(auditTotal - auditLogs.length)?.toLocaleString() ?? '0'} remaining)`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* 
            LEGAL TAB
         */}
        {activeTab === 'legal' && (
          <div className="sa-page">
            <div style={{ marginBottom: 28 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.sage, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>Legal</p>
              <h1 style={{ fontFamily: DISPLAY, fontSize: 34, fontWeight: 400, color: C.ink, lineHeight: 1.15 }}>Legal &amp; <em>Compliance</em></h1>
            </div>

            <div style={{ background: '#FFF3CD', border: '1.5px solid #FFC107', borderRadius: 14, padding: '16px 20px', marginBottom: 28, display: 'flex', gap: 12 }}>
              <span style={{ fontSize: 20 }}></span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#856404', marginBottom: 3 }}>Attorney Review Required</div>
                <div style={{ fontSize: 13, color: '#856404', lineHeight: 1.5 }}>These documents are comprehensive and specific to ClearPath PT but should be reviewed by a licensed healthcare attorney before use with real clinics. Budget $1,000–$3,000 for a one-time review.</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16, marginBottom: 28 }}>
              {LEGAL_DOCS.map(doc => (
                <div key={doc.id} style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 16, padding: '20px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 28, lineHeight: 1 }}>{doc.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{doc.title}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: doc.urgency === 'critical' ? C.redLt : C.blueLt, color: doc.urgency === 'critical' ? C.red : C.blue, textTransform: 'uppercase' }}>
                          {doc.urgency === 'critical' ? 'Critical' : 'Required'}
                        </span>
                      </div>
                      <p style={{ fontSize: 12, color: C.inkLt, lineHeight: 1.5, margin: 0 }}>{doc.description}</p>
                    </div>
                  </div>
                  <a href={`/legal/${doc.file}`} download style={{ display: 'block', textAlign: 'center', padding: '9px 0', borderRadius: 8, background: C.pine, color: 'white', textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
                     Download .docx
                  </a>
                </div>
              ))}
            </div>

            {/* HIPAA Compliance checklist */}
            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, background: C.cream, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>HIPAA Compliance Checklist</span>
                <span style={{ fontSize: 12, color: C.inkLt }}>— {COMPLIANCE_CHECKLIST.filter(i => i.done).length}/{COMPLIANCE_CHECKLIST.length} complete</span>
                <div style={{ marginLeft: 'auto', height: 6, width: 120, borderRadius: 99, background: C.foam, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round(COMPLIANCE_CHECKLIST.filter(i => i.done).length / COMPLIANCE_CHECKLIST.length * 100)}%`, background: C.green, borderRadius: 99 }} />
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                {COMPLIANCE_CHECKLIST.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', borderBottom: i < COMPLIANCE_CHECKLIST.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, background: item.done ? C.greenLt : C.foam, border: `2px solid ${item.done ? C.green : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: C.green, fontWeight: 700 }}>
                      {item.done ? '' : ''}
                    </div>
                    <span style={{ fontSize: 13, color: item.done ? C.ink : C.inkLt, fontWeight: item.done ? 500 : 400 }}>{item.label}</span>
                    {!item.done && <span style={{ marginLeft: 'auto', fontSize: 11, color: C.amber, fontWeight: 700, background: C.amberLt, padding: '2px 8px', borderRadius: 99 }}>To Do</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Signed Agreements tracker */}
            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, background: C.cream, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Signed Agreements by Clinic</span>
                {agreementsLoading
                  ? <span style={{ fontSize: 12, color: C.inkLt }}>Loading…</span>
                  : <span style={{ fontSize: 12, color: C.inkLt }}>— {agreements.filter(a => a.signed).length}/{agreements.length} clinics signed</span>
                }
                <button onClick={fetchAgreements} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'white', cursor: 'pointer', color: C.inkMid }}>Refresh</button>
              </div>
              {agreementsLoading ? (
                <div style={{ padding: '20px 24px', fontSize: 13, color: C.inkLt }}>Loading…</div>
              ) : agreements.length === 0 ? (
                <div style={{ padding: '20px 24px', fontSize: 13, color: C.inkLt }}>No clinics found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.foam }}>
                      {['Clinic', 'Slug', 'Owner Email', 'Registered', 'BAA + ToS Signed', 'Status'].map(h => (
                        <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontWeight: 700, color: C.inkMid, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {agreements.map((a, i) => (
                      <tr key={a.id} style={{ background: i % 2 === 0 ? 'white' : C.cream, borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '10px 16px', fontWeight: 600, color: C.ink }}>{a.name}</td>
                        <td style={{ padding: '10px 16px', color: C.inkMid, fontFamily: 'monospace', fontSize: 12 }}>{a.slug}</td>
                        <td style={{ padding: '10px 16px', color: C.inkMid }}>{a.owner_email || '—'}</td>
                        <td style={{ padding: '10px 16px', color: C.inkLt, fontSize: 12 }}>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: '10px 16px', color: a.signed ? C.green : C.inkLt, fontSize: 12 }}>
                          {a.terms_agreed_at ? new Date(a.terms_agreed_at).toLocaleString() : '—'}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          {a.signed
                            ? <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: C.greenLt, color: C.green, border: `1px solid ${C.green}` }}>Signed</span>
                            : <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: C.amberLt, color: C.amber, border: `1px solid ${C.amber}` }}>Pending</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* What you need to legally sell */}
            <div style={{ background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 20, padding: '24px 28px' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 14 }}>What You Need to Legally Sell</p>
              {[
                { item: 'LLC or Corporation registered',                      note: 'Register in Virginia via SCC website — ~$100' },
                { item: 'BAA signed with every clinic before PHI access',     note: 'Automate in your registration flow' },
                { item: 'Privacy Policy published at /privacy',               note: 'Required by law' },
                { item: 'Terms of Service published at /terms',               note: 'Required for enforcement' },
                { item: 'HIPAA Security Risk Assessment on file',             note: '45 C.F.R. § 164.308(a)(1) — use Compliancy Group or HIPAA One' },
                { item: 'AWS BAA executed',                                   note: 'Already done — HIPAA-eligible services' },
                { item: 'Cyber liability insurance ($1M+ recommended)',       note: 'Most enterprise clinics will ask for this' },
                { item: 'Stripe for billing (handles PCI-DSS)',               note: 'Never store card numbers yourself' },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: i < 7 ? `1px solid ${C.border}` : 'none', alignItems: 'flex-start' }}>
                  <span style={{ color: C.sage, fontSize: 14, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>→</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{row.item}</div>
                    <div style={{ fontSize: 11, color: C.inkLt }}>{row.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
